<template>
  <div>
    <!-- Sorting -->
    <div v-if="isActiveFilters">
      <div class="sorting" :title="sortingTitle">
        <strong>{{ appTranslate('Sorted by') }}: </strong>

        <template v-if="store.settings?.app.enableSortingInActiveFilters">
          <ImprovedCustomDropdown
            v-model="sortPropertyModel"
            class="d-inline-flex custom-dropdown"
            inner-class="pl-2 p-0"
            fixed
            discreet-inputs
            :deletable="false"
            :options="sortOptions"
          />

          <select
            v-if="defaultSortProperty"
            id="sort-order"
            :value="props.query.sortOrder"
            class="form-control"
            @input="sortBy(sortProperty || defaultSortProperty, ($event as any).target.value)"
          >
            <option hidden value="">{{ translate('ascending', 'application', 'lowercase') }}</option>
            <option value="asc">{{ translate('ascending', 'application', 'lowercase') }}</option>
            <option value="desc">{{ translate('descending', 'application', 'lowercase') }}</option>
          </select>
        </template>

        <template v-else>
          <span>{{ sortProperty?.header || props.query.sortFieldPath || translate(DefaultProductSorting.sortFieldPath, 'attributes', 'capitalize') }}</span>
          <i v-if="defaultSortProperty" class="ml-1 fa" :class="props.query.sortOrder === 'desc' ? 'fa-long-arrow-down' : 'fa-long-arrow-up'" />
        </template>
        <button v-if="!isDefaultProductSorting" class="reset" @click="resetSorting()">
          <i class="fa fa-undo" :title="appTranslate('Reset to default')" />
        </button>
      </div>
    </div>

    <!-- Filter -->
    <div v-if="activeFilters.length" class="filters-list" :style="maxHeight ? `max-height: ${maxHeight}` : ''">
      <strong> {{ appTranslate(isActiveFilters ? 'Showing products matching' : 'Filters') }}: </strong>

      <div class="d-contents" :title="activeFiltersString">
        <span v-for="(filter, i) of activeFilters" :key="i" class="filter" :class="filter.operator">
          <span class="header">
            <span>{{ filter.name }}</span>

            <img v-if="filter.language" class="small-flag language mb-1 ml-1" :src="getFlag(undefined, filter.language)" />
            <img v-else-if="filter.country" class="small-flag mb-1 ml-1" :src="getFlag(filter.country)" />
          </span>

          <span v-for="(option, j) of filter.options" :key="j" class="option">
            <button :disabled="!isActiveFilters" class="tag" :style="`box-shadow: 1px 2px 1px ${getColour(option)}`" @click="removeFilter(filter.id, j)">
              <em v-if="option.prefix" class="prefix">
                {{ option.prefix }}
              </em>
              <span v-if="option.parentName" class="prefix parent">
                {{ option.parentName }}
                &raquo;
              </span>
              <em v-if="option.isWildCard">
                {{ option.name }}
              </em>
              <template v-else>
                {{ option.name }}
              </template>
            </button>
            <!-- v-html="option.name" -->
            <!-- <span v-else class="tag" :style="`box-shadow: 1px 1px 1px ${getColour(option)}`" v-html="option.name" /> -->
          </span>
        </span>
      </div>

      <router-link v-if="isActiveFilters" class="text-nowrap ml-2" :to="{ name: 'products', query: getBaseProductsQuery() }">
        {{ appTranslate('Clear filters') }}
      </router-link>
    </div>
    <em v-else>
      {{ appTranslate('No active filters') }}
    </em>

    <template v-if="showIncludedCountries && includedCountries">
      <div class="d-flex align-items-center" style="gap: 1px">
        <small> {{ appTranslate('Includes data for') }}:&nbsp; </small>

        <img
          v-for="(country, i) of includedCountries"
          :key="i"
          :src="getFlag(country)"
          :class="[country === dataCulture?.country ? 'small-flag' : 'tiny-flag']"
          :title="country.toUpperCase()"
        />

        <i v-if="includeProductsMissingAvailableCountries" class="tiny-flag no-flag" />
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import ImprovedCustomDropdown from '@/components/ImprovedCustomDropdown.vue'
import { DefaultProductSorting, useSorting } from '@/composables/useSorting'
import { useTools } from '@/composables/useTools'
import { useTranslation } from '@/composables/useTranslation'
import { Filter, FilterOperator, FilterOption, FilterProperties, NoneKey, WildcardKey } from '@/models/Filter'
import { Country, Culture, Language, Option } from '@/models/Global'
import { AdvancedProperty } from '@/models/Property'
import { SimplifiedAttribute } from '@/models/Taxonomy'
import DataService from '@/services/DataService'
import PropertyService from '@/services/PropertyService'
import { useDefaultStore } from '@/store'
import { useTaxonomiesStore } from '@/store/taxonomiesStore'
import { Debouncer } from '@/utils/Debouncer'
import { decodeQueryValue, getBaseProductsQuery } from '@/utils/filterUtils'
import { camelCaseToTerm, decodeHtmlEntities, isValidCssColour, sumNonEmptyObjects, toSafeString } from '@/utils/tools'
import { computed, onMounted, ref, watch } from 'vue'
import { LocationQuery, onBeforeRouteUpdate, useRouter } from 'vue-router'

// -----------
// -- Setup --
// -----------
const props = defineProps<{
  query: LocationQuery
  dataCulture: Culture | null
  includedCountries?: Iterable<Country>
  isActiveFilters?: boolean
  maxHeight?: string
  includeProductsMissingAvailableCountries?: boolean
  showIncludedCountries?: boolean
}>()

const store = useDefaultStore()
const { translate, appTranslate } = useTranslation()
const { getFlag } = useTools()
const router = useRouter()
const { isDefaultProductSorting, resetSorting, sortBy } = useSorting()

const fetchDebouncer = new Debouncer()

// ---------------
// -- Reactives --
// ---------------
const availableFilters = ref<Filter[]>([])
const properties = ref<AdvancedProperty[]>([])
const sortProperty = ref<AdvancedProperty>()

// -------------------------
// -- Computed properties --
// -------------------------
const activeFilters = computed((): Filter[] => {
  const getTranslatedTerm = (value: string) => translate(value, ['terms', 'application'], 'capitalize')

  return Object.entries(props.query)
    .filter(([key, value]) => {
      return key?.startsWith('_') && toSafeString(value)?.split(',').filter(Boolean).length
    })
    .map(([key, value]) => {
      const values = value?.toString().split(',') || []

      if (key === '_q') {
        // --------------------
        // -- Search filters --
        // --------------------
        const exactMatch = !!props.query.exactSearch
        return {
          id: '_q',
          name: [appTranslate('Search filter'), exactMatch ? `(${translate('exact match', 'application', 'lowercase')})` : ''].filter(Boolean).join(' '),
          options:
            values?.map((value) => ({
              name: value,
              id: value,
            })) || [],
        }
      } else if (key.startsWith('_field') || key.startsWith('_meta') || key.startsWith('_computed')) {
        // --------------------------------
        // -- Basic field / meta filters --
        // --------------------------------
        const data = key.split('_')
        const path = data?.[2]
        const fieldPath = path?.split('.')
        const operator = data?.[3] as FilterOperator

        const field = properties.value.find((prop) => prop.sortFieldPath === path)?.defaultName || fieldPath?.[fieldPath.length - 1]

        let language, country

        if (fieldPath?.[0]?.toLowerCase() === 'languagespecific' && fieldPath?.[1]) {
          language = fieldPath[1] as Language
        } else if (fieldPath?.[0]?.toLowerCase() === 'countryspecific' && fieldPath?.[1]) {
          country = fieldPath[1] as Country
        }

        return {
          id: key,
          name: translate(camelCaseToTerm(field), ['attributes'], 'capitalize'),
          language,
          country,
          operator,
          options:
            values.reduce((acc: FilterOption[], value, i) => {
              const name = getTranslatedTerm(value)

              let prefix
              if (operator === 'range') {
                prefix = translate(i == 0 ? 'from' : i == 1 ? 'to' : '', 'application', 'lowercase')
              } else if (operator !== 'equals') {
                prefix = translate(operator, 'application', 'lowercase')
              }

              if (name && value) {
                acc.push({
                  id: value,
                  prefix,
                  name: decodeQueryValue(name),
                })
              }
              return acc
            }, []) || [],
        }
      } else {
        // -----------------------
        // -- Taxonomic filters --
        // -----------------------
        const [, taxonomy, id, operator] = key.split('_')

        const foundFilter = availableFilters.value.find((availableFilter) => availableFilter.compareId === id || availableFilter.compareId === taxonomy)
        let altAttribute: SimplifiedAttribute | undefined
        if (!foundFilter) {
          altAttribute = useTaxonomiesStore().taxonomies.attributes.find((attribute) => attribute.id === id)
        }

        const activeOptions = values?.reduce((acc: FilterOption[], value) => {
          const foundOption = foundFilter?.options.find((o) => o.id === value)
          const foundParentName = foundOption?.parentId ? foundFilter?.options.find((o) => o.id === foundOption.parentId)?.name : undefined

          const foundTerm = altAttribute?.hasTerms ? altAttribute?.terms?.find((term) => term.id === value) : undefined

          let name = foundTerm?.name?.toString() ?? foundOption?.name ?? decodeQueryValue(getTranslatedTerm(value))
          let isWildCard = false
          let prefix

          if (value === NoneKey || value === WildcardKey) {
            isWildCard = true
            name = appTranslate(value)
          }

          if (operator && operator !== 'equals') {
            name = value
            prefix = translate(operator, 'application', 'lowercase')
          }

          if (name && value) {
            acc.push({
              id: value,
              name,
              defaultName: foundTerm?.defaultName.toString() || foundOption?.defaultName,
              prefix,
              parentName: foundParentName,
              isWildCard,
              operator: operator as FilterOperator,
            })
          }
          return acc
        }, [])

        return {
          name: foundFilter?.name || altAttribute?.name.toString() || translate(id, ['attributes', 'application'], 'capitalize'),
          id: foundFilter?.id || key,
          options: activeOptions,
        }
      }
    })
})

const sortPropertyModel = computed({
  get() {
    const property =
      properties.value.find((property) => property.sortFieldPath === props.query.sortFieldPath) ||
      properties.value.find((property) => property.sortFieldPath === DefaultProductSorting.sortFieldPath)
    return property?.defaultHeader
  },
  set(value) {
    sortProperty.value = properties.value.find((property) => property.defaultHeader === value)
    if (sortProperty.value) {
      sortBy(sortProperty.value)
    }
  },
})
const defaultSortProperty = computed(() => properties.value.find((property) => property.sortFieldPath === DefaultProductSorting.sortFieldPath))

const sortingTitle = computed(() => {
  const sortField = sortProperty.value?.header || props.query.sortFieldPath || translate(DefaultProductSorting.sortFieldPath, 'attributes', 'capitalize')
  const sortOrder = translate(toSafeString(props.query.sortOrder) || 'asc', 'application', 'lowercase')
  return `${appTranslate('Sorting')}: ${sortField}, ${sortOrder}`
})

const activeFiltersString = computed(() => {
  const string = activeFilters.value
    ?.map((filter) => {
      const joiner = filter.operator == 'range' ? ' ' : ` ${appTranslate('OR', undefined, 'uppercase')} `
      const binder = filter.operator === 'equals' || filter.operator == 'range' ? ` ${appTranslate('is', undefined, 'lowercase')} ` : ' '
      const valueString = filter.options.map((o, i) => `${o.prefix ?? ''} "${decodeHtmlEntities(o.name)}"`.trim()).join(joiner)
      return filter.name.toLowerCase() + binder + valueString
    })
    .join(` ${appTranslate('AND', undefined, 'uppercase')} `)

  return `${appTranslate('Showing products where')} ${string}`
})

const sortOptions = computed(
  () =>
    properties.value.reduce((acc: Option<string>[], property) => {
      if ((property.type !== 'countrySpecific' && property.type !== 'languageSpecific') || property.culturePrefix) {
        acc.push({ value: property.defaultHeader, displayValue: property.header || property.defaultHeader })
      }

      return acc
    }, []),

  // .sort((a, b) => a.displayValue.localeCompare(b.displayValue)),
)

// --------------
// -- Watchers --
// --------------

watch(
  () => store.overrideIncludeAllCultures,
  async () => {
    availableFilters.value = await DataService.getAvailableFilters(router.currentRoute.value.query)
  },
)

// ----------------------
// -- Life cycle hooks --
// ----------------------
onBeforeRouteUpdate((to, from, next) => {
  if (to.name === 'products') {
    fetchDebouncer.debounce(() => {
      DataService.getAvailableFilters().then((filters) => (availableFilters.value = filters))
    }, store.cultureChangeFetchDelay)
  }
  next()
})

onMounted(() => {
  DataService.getAvailableFilters(router.currentRoute.value.query).then((filters) => (availableFilters.value = filters))
  PropertyService.getPropertyGroups('products').then((propGroups) => (properties.value = propGroups.flat())) // should include all that filters includes and more (for sorting)
})

// ---------------
// -- Functions --
// ---------------
const removeFilter = (queryKey: string, optionIndex: number) => {
  if (props.isActiveFilters) {
    const query = { ...props.query }
    const currentValues = query[queryKey]?.toString().split(',')

    if (!currentValues) {
      return
    }

    if (queryKey.includes('range')) {
      if (sumNonEmptyObjects(currentValues) > 1) {
        query[queryKey] = currentValues.map((value, i) => (i === optionIndex ? '' : value)).join(',')
      } else {
        delete query[queryKey]
      }
    } else {
      currentValues.splice(optionIndex, 1)
      query[queryKey] = currentValues.join(',')
    }

    if (!String(query[queryKey] || '')?.replace(',', '').length) {
      delete query[queryKey]
    }

    router.push({
      query,
      force: true,
    })
  }
}

const getColour = (option: FilterProperties) => (option?.defaultName && isValidCssColour(option.defaultName) ? option.defaultName : '#bbb')
</script>

<style lang="scss" scoped>
.filters-list {
  overflow-y: auto;
}

.filter {
  font-style: italic;
  // color: var(--success);
  color: #aaa;
  margin: 0 1px;

  &:not(:last-child)::after {
    content: '&';
    color: var(--danger);
    font-size: 1.1em;
    margin-left: 0.2em;
    margin-right: 0.4em;
  }

  .header::after {
    content: ':';
  }

  &:not(.range) .option:not(:last-child)::after {
    content: '|';
    color: var(--success);
    font-size: 1.1em;
    margin: 0 0.2em;
  }
}

.tag {
  &:disabled {
    color: var(--text-colour) !important;
    font-style: italic;
  }

  .prefix {
    margin: 0.25rem 0.25rem 0 0;
    color: #999;
    font-size: 0.9em;

    &.parent {
      color: #666;
    }
  }
}

.sorting {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;

  .custom-dropdown {
    width: min(320px, 100vw);
    max-height: 1.8em;
  }

  select {
    width: fit-content;
    display: inline-block;
    padding: 0;
    max-height: 1.8em;
  }

  .reset {
    // position: absolute;
    border-radius: 8px;
    border: 1px solid #aaa;
    padding: 0 4px;
  }
}
</style>
